'use client';

import { motion } from 'motion/react';
import { useCallback, useSyncExternalStore } from 'react';

import { ArrowUpIcon } from '../ui/arrow-up';
import { Button } from '../ui/button';

const SCROLL_THRESHOLD = 300;

function subscribeToScroll(onStoreChange: () => void) {
  const handler = () => {
    const visible = window.scrollY > SCROLL_THRESHOLD;
    if (visible !== scrollVisibleRef.current) {
      scrollVisibleRef.current = visible;
      onStoreChange();
    }
  };
  scrollVisibleRef.current = window.scrollY > SCROLL_THRESHOLD;
  if (scrollVisibleRef.current) onStoreChange();
  window.addEventListener('scroll', handler, { passive: true });
  return () => window.removeEventListener('scroll', handler);
}

const scrollVisibleRef = { current: false };

function subscribeToReducedMotion(onStoreChange: () => void) {
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  mq.addEventListener('change', onStoreChange);
  return () => mq.removeEventListener('change', onStoreChange);
}

function getReducedMotionSnapshot() {
  return typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

const ScrollToTop = () => {
  const isVisible = useSyncExternalStore(
    subscribeToScroll,
    () => scrollVisibleRef.current,
    () => false,
  );

  const prefersReducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    getReducedMotionSnapshot,
    () => false,
  );

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  }, [prefersReducedMotion]);

  return (
    <div className='fixed right-4 bottom-6 z-50 sm:right-6 sm:bottom-8'>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{
          opacity: isVisible ? 1 : 0,
          y: prefersReducedMotion ? 0 : isVisible ? 0 : 16,
          pointerEvents: isVisible ? 'auto' : 'none',
        }}
        transition={
          prefersReducedMotion ? { duration: 0 } : { duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }
        }
        aria-hidden={!isVisible}
      >
        <Button
          onClick={scrollToTop}
          aria-label='Scroll to top'
          tabIndex={isVisible ? 0 : -1}
          size='icon'
          className='size-12 rounded-full bg-linear-to-r from-pink-500 to-violet-600 text-white shadow-lg shadow-violet-500/25 transition-transform hover:scale-105 hover:from-pink-400 hover:to-violet-500 focus-visible:ring-violet-400/50'
        >
          <ArrowUpIcon size={22} className='font-bold' />
        </Button>
      </motion.div>
    </div>
  );
};

export default ScrollToTop;
